import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DropdownProps } from '../../../types/DesignSystem/Dropdown';
import FieldTitle from '../FieldTitle/FieldTitle';
import Typography from '../Typography/Typography';
import InfoTooltip from '../Tooltip/InfoTooltip';
import Tooltip from '../Tooltip/Tooltip';
import InfoGlyph from '../Tooltip/InfoGlyph';
import Spinner from '../Spinner/Spinner';
import { FOCUS_FIELD, FOCUS_RING, FOCUS_RING_INSET } from '../focusRing';
import { FIELD_BOX, fieldBorder, FIELD_REST, FIELD_DISABLED } from '../fieldChrome';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';
import { useDebouncedCallback } from '../debounce';
import { Check, ChevronDown, Cross } from '../Iconography/Icons';
import { CONTROL_HEIGHT, FIELD_INSET } from '../controlSize';
import { useDismissable } from '../useDismissable';
import { firstTypeaheadMatch, handleListNavKey, seedTypeahead } from '../listKeyboardNav';
import { retainingDisabled, useFocusHolder } from '../focusRetention';
import { announceResultCount, useSettledWords } from '../liveRegion';
import { useMergedRef } from '../useMergedRef';
import { MOTION } from '../motion';
import { usePresence } from '../usePresence';
import { POPOVER_ROOM_PX, fixedAnchorStyle, preferUpward, samePosition, useAnchorUpdates } from '../useAnchorUpdates';
import { useControlSize } from '../DensityProvider/DensityProvider';

const Dropdown = React.forwardRef<HTMLButtonElement, DropdownProps>(function Dropdown({
  label,
  options,
  value: valueProp,
  defaultValue,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  placeholder = 'Select an option',
  additionalInfo = '',
  infoMessage = '',
  className = '',
  size: sizeProp,
  labelSize,
  /* § 304 — portalled by DEFAULT (see the type file); `false` is the inline escape hatch. */
  usePortal = true,
  searchable = false,
  onSearchChange,
  debounceMs,
  onClear,
  onScrollEnd,
  loading = false,
  searchPlaceholder = 'Search',
  renderOption,
  'data-testid': dataTestId,
  'aria-describedby': describedByProp,
  name,
  onFocus: onFocusProp,
  ...rest
}, forwardedRef) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  /*
    The trigger is a `<button>`, named with `aria-labelledby` — pointing at the label
    TEXT only (not the row, which also holds the required marker and the info tooltip),
    plus the selected value since § 374. (§ 383 — this said a `<label htmlFor>` could not
    point at a button. It can: a button is labelable, and Toggle's label activates its
    switch since § 382. The trigger still needs `aria-labelledby`, because its name must
    carry the value as well as the label.)

    `aria-controls` was missing: the trigger declared `aria-haspopup="listbox"` and
    `aria-expanded` but never said WHICH listbox, so assistive technology had no way
    to move to it. `helperText` was likewise unreferenced, so an errored dropdown
    announced "invalid" with no reason (WCAG 3.3.1).
  */
  const uid = React.useId();
  const labelId = `${uid}-label`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  const infoId = `${uid}-info`;
  /* § 356 / § 357 — see TextField. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState<string | number | undefined>(valueProp, defaultValue, undefined);
  const listboxId = `${uid}-listbox`;
  /* § 337 — see the trigger's `aria-describedby`. */
  const requiredId = `${uid}-required`;
  /* § 374 — the id of the trigger's own value text; see `triggerLabelledBy` below. */
  const valueId = `${uid}-value`;
  const [isOpen, setIsOpen] = useState(false);
  /* § 242 — the listbox stays mounted for its exit (`popup-rise-out`), exactly as
     Dialog does through the same hook; while exiting it is aria-hidden and inert. */
  const { isPresent: listboxPresent, phase: listboxPhase } = usePresence(isOpen && !disabled, MOTION.popover.exitMs);
  const exiting = listboxPhase === 'exiting';
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  /* § 242 — the flip decision, for BOTH paths (portal `bottom:` and inline
     `bottom-full`), measured at open and re-measured while open. */
  const [openUp, setOpenUp] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Merged, not substituted: `buttonRef` is what `calculatePosition` measures to
  // decide whether the menu opens upward.
  const setButtonRef = useMergedRef<HTMLButtonElement>(buttonRef, forwardedRef);

  const selectedOption = options.find((opt) => opt.value === value);
  /*
    § 374 — the trigger's name carries the selected VALUE. `aria-labelledby` replaces a
    button's own text as its name, so pointing it at the label alone announced "Category,
    button" and never the value on the trigger's face (WCAG 4.1.2). With a selection it points
    at the label AND the value text — the APG collapsible-listbox arrangement — so the visible
    label still leads the name (WCAG 2.5.3). With nothing selected the label alone names it:
    a placeholder is not a value. A label-less trigger named by `aria-label` keeps that name
    and is DESCRIBED by the value instead; one with neither is named by its own text, as always.
  */
  const labelled = label !== '';
  const ariaLabelName = rest['aria-label'];
  const namedByAriaLabel = !labelled && typeof ariaLabelName === 'string' && ariaLabelName.trim() !== '';
  const triggerLabelledBy = labelled ? (selectedOption ? `${labelId} ${valueId}` : labelId) : undefined;
  /* § 303 — the filter box reports through one debounced channel (synchronous when
     `debounceMs` is unset, which is every consumer today). */
  const search = useDebouncedCallback<[string]>(onSearchChange, debounceMs);
  /* § 401 — whether the trigger holds focus: a trigger disabled while it does keeps it (a
     page-size Dropdown disabled for the refetch its own pick started). */
  const { holder, bind } = useFocusHolder<'trigger'>();
  const triggerFocus = bind('trigger');
  /* § 303 — the clear × exists only while there is a value to clear and a caller to tell. */
  const showClear = typeof onClear === 'function' && !disabled && selectedOption !== undefined;

  /* § 242 — ONE measurement for flip + position. Called at open (so the first paint
     is already placed, as before) and by `useAnchorUpdates` on every scroll/resize
     while the listbox is up — a portalled listbox no longer floats where its trigger
     used to be after the page scrolls under it. § 304 — the arithmetic moved to
     `fixedAnchorStyle`, shared with Combobox, Popover and DropdownButton; the z is the
     ladder's `popover` rung (it was `tooltip`, 9999, when no rung existed for it).
     `preferUpward` (§ 242) also asks whether there is MORE room above — flipping a
     trigger near the top of a short viewport upward was flipping it into less space. */
  const measure = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const above = preferUpward(rect, POPOVER_ROOM_PX);
    setOpenUp(above);
    if (usePortal) {
      // § 334 — publish the room left on that side (the list caps at it) and hide the list
      // when its trigger has been scrolled out of view.
      const next = fixedAnchorStyle(rect, { above, matchWidth: true, fit: true, anchorEl: buttonRef.current });
      setMenuStyle((prev) => (prev && samePosition(prev, next) ? prev : next));
    }
  }, [usePortal]);

  const listboxRef = useRef<HTMLDivElement>(null);

  /* § 65 search state. Reset on close so reopening always shows the full list —
     a stale filter with a cleared-looking trigger is a "where did my options go". */
  const [query, setQuery] = useState('');
  React.useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;

    if (!isOpen) measure();
    setIsOpen(!isOpen);
  };

  useDismissable({
    isOpen,
    onDismiss: (reason) => {
      setIsOpen(false);
      /* § 406 — Escape hands focus back to the trigger (the APG listbox popup rule, and what
         Menu and DropdownButton already did). With focus on an option or in the filter box, the
         list unmounted under it and focus fell to <body> (measured in Chromium). An outside
         click leaves focus where the click put it. `onBlur` reports the dismissal as before. */
      if (reason === 'escape') buttonRef.current?.focus();
      onBlur?.();
    },
    // With usePortal the listbox is not a DOM descendant of the trigger, so
    // containment alone would treat every click inside the menu as "outside".
    // § 304 — scoped to THIS dropdown's marker: a click in another portalled surface
    // is outside, as it should be.
    isInside: (target) =>
      !!containerRef.current?.contains(target) ||
      (!!usePortal && !!(target as Element).closest?.(`[data-dropdown-portal="${uid}"]`)),
  });

  /*
    Focus an option once the listbox has actually rendered. § 406 — `typed`: the first option
    starting with that text (else the selected one), and the list's type-ahead search starts
    from it.

    § 406 — and only once the list EXISTS. This was one requestAnimationFrame after the key, on
    the guess that the list would have rendered by then. In Chromium it had not: the list mounts
    a render later (usePresence, then the portal's first measurement), so the frame found no
    list and did nothing. ArrowDown on a closed Dropdown opened it and left focus on the trigger
    (measured in the browser suite on the original code); jsdom flushed both in time, so every
    unit test passed. The request now waits in a ref, and the layout effect below applies it on
    the first commit that has the list. A request made while the list is already open is applied
    by the frame, since moving focus inside an open list renders nothing. Closing drops it, so a
    request left over from a list that closed first never pulls focus into a closing surface (the
    § 386 Menu race).
  */
  const pendingFocus = useRef<{ which: 'first' | 'last' | 'selected'; typed?: string } | null>(null);
  const applyPendingFocus = () => {
    const request = pendingFocus.current;
    const list = listboxRef.current;
    if (!request || !list) return;
    pendingFocus.current = null;
    const options = [...list.querySelectorAll<HTMLElement>(
      '[role="option"]:not([disabled]):not([aria-disabled="true"])',
    )];
    if (options.length === 0) return;
    const selected = options.find((o) => o.getAttribute('aria-selected') === 'true');
    const match = request.typed ? firstTypeaheadMatch(options, request.typed) : undefined;
    if (request.typed) seedTypeahead(list, request.typed);
    const target =
      match ? match
      : request.which === 'last' ? options[options.length - 1]
      : request.which === 'selected' ? (selected ?? options[0])
      : options[0];
    target.focus();
  };
  const focusOption = (which: 'first' | 'last' | 'selected', typed?: string) => {
    pendingFocus.current = { which, typed };
    requestAnimationFrame(applyPendingFocus);
  };
  React.useLayoutEffect(() => {
    if (!isOpen) {
      pendingFocus.current = null;
      return;
    }
    applyPendingFocus();
  });

  const renderOptionRow = (option: (typeof options)[number]) => {
    /*
      An option's tooltip describes THE OPTION, and the option button is already
      the focusable element — so the tooltip wraps the button and the "ⓘ" stays a
      decorative glyph. It used to be an `OverlayIcon` inside the button, which
      made the description pointer-only and unreachable by anyone arrow-keying
      the list. Making it an `InfoTooltip` instead would have put a `<button>`
      inside a `<button>`: invalid HTML, and a second tab stop per option.
    */
    const optionButton = (
      <button
        type="button"
        role="option"
        tabIndex={-1}
        aria-selected={value === option.value}
        /* `aria-disabled`, not the native attribute: a natively disabled button leaves
           the accessibility tree entirely, and an option the user cannot choose is
           exactly the thing they need TOLD about. The arrow-key walker skips it via the
           `:not([aria-disabled="true"])` filter above. */
        aria-disabled={option.disabled || undefined}
        data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
        onClick={(event) => {
          if (option.disabled) return;
          setValue(option.value);
          onChange?.(option.value);
          setIsOpen(false);
          /* § 304 — a KEYBOARD pick (Enter on a focused option: `detail === 0`) hands
             focus back to the trigger, so the list unmounting does not drop it to
             the body. A mouse pick leaves focus where the click put it, as before. */
          if (event.detail === 0) buttonRef.current?.focus();
          onBlur?.();
        }}
        /* Selected is BRAND, hover is neutral — the split that lets the list read
           at a glance, and the one the current Atlassian theme runs (selected
           #E9F2FE + blue ink vs a neutral hover; our blue-100 is within 1.02:1 of
           their tint). `accent` on blue-100 measures 15.82:1. Before this the
           selected row was grey-on-grey — the same treatment as a hover, so the
           list carried no memory of what you had picked. The hover class lives in
           the UNSELECTED branch only: on the selected row it would fight
           `tw-bg-blue-100` and leave the winner to stylesheet order. */
        className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-text-left tw-text-body-primary tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING_INSET}
          ${option.disabled
            ? 'tw-text-ink-disabled tw-cursor-not-allowed'
            : value === option.value
            ? 'tw-bg-accent-tint tw-text-accent'
            : 'tw-text-ink-secondary hover:tw-bg-surface-hover-strong active:tw-bg-tone-neutral-tint hover:tw-text-ink'
          }
        `}
      >
        <span className="tw-flex tw-gap-2 tw-items-center">
          {/* § 385 — the caller's content, inside the component's row. */}
          {renderOption ? renderOption(option, { selected: value === option.value, disabled: Boolean(option.disabled) }) : (
            <>
              {/*
                Decorative — the label carries the meaning (see `DropdownOption.icon`).
                `aria-hidden` here rather than at the call site so a caller cannot
                forget it and add a second, competing name to the option.
              */}
              {option?.icon && (
                <span aria-hidden="true" className="tw-flex tw-items-center tw-flex-shrink-0">
                  {option.icon}
                </span>
              )}
              {option.label}
              {option?.tooltip && <InfoGlyph className="tw-flex-shrink-0 tw-text-ink-tertiary" />}
            </>
          )}
        </span>
        {value === option.value && (
          /* § 232 — the set's Check at the mark grid, not a private 12×8/1.4 drawing:
             the same mark Toggle shows at this exact size. aria-hidden — the row's
             aria-selected is the fact. */
          <Check size={12} aria-hidden="true" className="tw-ml-2 tw-flex-shrink-0" />
        )}
      </button>
    );

    return option?.tooltip ? (
      // `block`, or the inline-block wrapper would shrink a full-width option.
      // `usePortal`, because the listbox is `overflow-y-auto` and would clip it.
      <Tooltip key={option.value} content={option.tooltip} placement="right" usePortal block>
        {optionButton}
      </Tooltip>
    ) : (
      <React.Fragment key={option.value}>{optionButton}</React.Fragment>
    );
  };

  /* § 65: `group` on consecutive options becomes one labelled `role="group"`
     (listbox → group → option is valid ARIA), with a rule between buckets. */
  const trimmedQuery = query.trim().toLowerCase();
  /* `onSearchChange` means the SERVER did the matching — see the prop's note on why
     re-filtering its result locally is a silent narrowing, not a no-op. */
  const visibleOptions = searchable && trimmedQuery && !onSearchChange
    ? options.filter((option) => option.label.toLowerCase().includes(trimmedQuery))
    : options;
  /*
    § 406 — a searchable Dropdown SAYS how many options its filter left, as Combobox has since
    § 396: the count is spoken once the query has been still for RESULTS_ANNOUNCE_MS, never
    while loading, never for an empty query. Its region mounts with the open list (empty) and
    the words arrive later, a change the screen reader hears. A server-filtered list
    (`onSearchChange`) is counted as it arrives.
  */
  const resultsSpoken = useSettledWords(
    searchable && isOpen && !loading && trimmedQuery !== '' ? announceResultCount(visibleOptions.length) : '',
  );
  const buckets: Array<{ label?: string; options: typeof options }> = [];
  for (const option of visibleOptions) {
    const lastBucket = buckets[buckets.length - 1];
    if (!lastBucket || lastBucket.label !== option.group) {
      buckets.push({ label: option.group, options: [option] });
    } else {
      lastBucket.options.push(option);
    }
  }

  const bucketRows = (
    <>
      {buckets.map((bucket, bucketIndex) => {
        const groupLabelId = bucket.label ? `${uid}-group-${bucketIndex}` : undefined;
        const bucketOptions = bucket.options.map(renderOptionRow);
        return (
          // Buckets are positional per render; the index is their identity.
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={bucketIndex}>
            {bucketIndex > 0 && <div className="tw-border-t tw-border-edge" />}
            {bucket.label ? (
              <div role="group" aria-labelledby={groupLabelId}>
                <div id={groupLabelId} className="tw-px-3 tw-pt-2 tw-pb-1">
                  <Typography variant="sub-title-label" className="tw-text-ink-tertiary">
                    {bucket.label}
                  </Typography>
                </div>
                {bucketOptions}
              </div>
            ) : (
              bucketOptions
            )}
          </React.Fragment>
        );
      })}
      {/*
        An empty listbox must SAY it is empty — for every dropdown, not only the
        searchable ones.

        This row was gated on `searchable`, on the reasonable-looking assumption that a
        list only becomes empty by being filtered. It also becomes empty by being handed
        `options={[]}`, and a plain dropdown in that state rendered a popover containing
        NOTHING: a 6px sliver of border under the trigger. Reported against ISR's Push to
        Environment, whose Target Environment list is the current environment filtered out
        of the account's environments — one environment configured, nothing to offer. It
        reads as a clipped or broken popover rather than as an answer, and the reporter
        reasonably filed it as the menu being cut off by the dialog.

        The copy splits because the two states are different facts: a filter that matched
        nothing invites you to retype, an empty source list does not.
      */}
      {visibleOptions.length === 0 && !loading && (
        <div
          className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink-tertiary"
          data-testid={dataTestId ? `${dataTestId}-no-options` : undefined}
        >
          {searchable && trimmedQuery ? 'No matching options' : 'No options'}
        </div>
      )}
      {loading && (
        <div
          className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink-tertiary tw-flex tw-items-center tw-justify-center tw-gap-2"
          data-testid={dataTestId ? `${dataTestId}-loading` : undefined}
        >
          <Spinner size="sm" aria-hidden="true" />
          Loading...
        </div>
      )}
    </>
  );

  /* `origin-top` (§ 219): popup-rise scales from 0.98 now, and a top origin makes
     the listbox grow out of its trigger rather than zoom in place. When the PORTAL
     path measured too little space below and opened UPWARD (§ 222), the origin
     flips with it — a menu above the trigger growing downward out of empty air is
     the exact defect the origin exists to prevent. `menuStyle` holds `bottom` only
     on that path, so the position and the origin cannot disagree. */
  const opensUpward = openUp;
  /* § 242: the entrance/exit pair from MOTION.popover; the exiting surface is inert
     (`pointer-events-none`) so a click during the 100ms fade lands on the page. The
     inline path flips too now (`bottom-full` above the trigger), which it never could. */
  const POPOVER_CHROME = `tw-bg-surface-raised tw-border tw-border-edge tw-rounded-srf tw-shadow-ds-md ${
    opensUpward ? 'tw-origin-bottom' : 'tw-origin-top'
  } ${exiting ? `${MOTION.popover.exit} tw-pointer-events-none` : MOTION.popover.enter}`;
  const POPOVER_POSITION = usePortal
    ? ''
    : `tw-absolute tw-left-0 tw-right-0 tw-z-10 ${opensUpward ? 'tw-bottom-full tw-mb-1' : 'tw-top-full tw-mt-1'}`;

  /* Near-bottom, not at-bottom: waiting for the exact last pixel means the fetch
     starts only once the user has already run out of list. 40px is roughly one row. */
  const SCROLL_END_THRESHOLD_PX = 40;
  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!onScrollEnd) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= SCROLL_END_THRESHOLD_PX) onScrollEnd();
  };

  /* § 304 — Tab leaves the LIST, not the page: the listbox closes and focus returns to
     the trigger, so the next Tab moves on from the control itself. Without this a
     portalled list (now the default) hands focus to the end of the document. */
  const leaveList = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return false;
    event.preventDefault();
    setIsOpen(false);
    buttonRef.current?.focus();
    return true;
  };

  const listNavKeyDown = (event: React.KeyboardEvent) => {
    if (leaveList(event)) return;
    handleListNavKey(event, {
      container: listboxRef.current,
      itemSelector: '[role="option"]',
      orientation: 'vertical',
      typeAhead: true,
    });
  };

  /* Searchable splits the popover in two — filter input above, listbox below —
     because an `<input>` is not a valid child of `role="listbox"`. Without
     `searchable` the listbox IS the popover, exactly the pre-§ 65 DOM. */
  const menuContent = searchable ? (
    <div
      /* § 304 — the marker carries THIS control's id, so its own outside-click guard
         and blur check can tell its surface from another's; Dialog's guard and the
         modal Tab cycle only test for the attribute's presence. */
      data-dropdown-portal={usePortal ? uid : undefined}
      style={usePortal ? menuStyle ?? undefined : undefined}
      /* § 242 — gone from the accessibility tree the instant it closes; the pixels
         follow 100ms later. */
      aria-hidden={exiting || undefined}
      /* § 415 — the ROOM caps the whole surface, not just the list under the filter.
         The listbox alone carried `min(16rem, room)`, so the ~53px filter header rode
         ON TOP of a list already as tall as the room: near the viewport's bottom the
         last rows sat off-screen (QA, Recategorize target picker, 2026-10-05). Now the
         surface is a column capped at the room, the header keeps its height, and the
         list (`min-h-0`, shrinkable) takes what is left — still never over 16rem. */
      className={`${POPOVER_CHROME} tw-overflow-hidden tw-flex tw-flex-col tw-max-h-[var(--ds-popover-room,none)] ${POPOVER_POSITION}`}
    >
      <div className="tw-p-2 tw-border-b tw-border-edge tw-shrink-0">
        <input
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            search.call(event.target.value);
          }}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          /* § 406 — the box names the list it filters. */
          aria-controls={listboxId}
          data-testid={dataTestId ? `${dataTestId}-search` : undefined}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the popover just opened by explicit user action; the filter is its purpose
          autoFocus
          onKeyDown={(event) => {
            if (leaveList(event)) return;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              focusOption('first');
            }
          }}
          /* § 296 — the field seam, not a private copy of it. This input hand-rolled
             its chrome: a `tw-h-9` literal for the control height, a bare border, no
             hover whisper, no transition (the one field in the system off the § 287
             ladder), and a focus that changed ONLY the border colour — 4.72:1 against
             the resting border in light, 2.07:1 in dark, under the 3:1 a state change
             needs (SC 1.4.11). `FOCUS_FIELD`'s docblock is about exactly that failure
             and answers it with the 1px ring every other field carries. `FIELD_BOX` +
             `fieldBorder` + `FIELD_REST` + the control grid give it all of that in one
             line; the § 224 transparent outline for forced-colors rides inside
             `FOCUS_FIELD`. `md` fixed rather than the trigger's `size`: a filter inside
             a popover shares no row with other controls, and the height it always had
             is the md rung. */
          className={`tw-w-full ${CONTROL_HEIGHT.md} ${FIELD_INSET.md} ${FIELD_BOX} ${fieldBorder({})} tw-text-body-primary tw-text-ink placeholder:tw-text-ink-secondary ${FIELD_REST}`}
        />
      </div>
      <div
        ref={listboxRef}
        onKeyDown={listNavKeyDown}
        onScroll={handleListScroll}
        id={listboxId}
        role="listbox"
        aria-labelledby={label !== '' ? labelId : undefined}
        /* `overscroll-contain` (§ 222): a wheel that reaches the end of the list
           must stop, not start scrolling the page under the popover. */
        className="tw-max-h-[16rem] tw-min-h-0 tw-overflow-y-auto tw-overscroll-contain"
      >
        {bucketRows}
      </div>
    </div>
  ) : (
    <div
      ref={listboxRef}
      onKeyDown={listNavKeyDown}
      data-dropdown-portal={usePortal ? uid : undefined}
      style={usePortal ? menuStyle ?? undefined : undefined}
      aria-hidden={exiting || undefined}
      id={listboxId}
      role="listbox"
      aria-labelledby={label !== '' ? labelId : undefined}
      className={`${POPOVER_CHROME} tw-max-h-[min(16rem,var(--ds-popover-room,16rem))] tw-overflow-y-auto tw-overscroll-contain ${POPOVER_POSITION}`}
    >
      {bucketRows}
    </div>
  );

  const shouldShowMenu = listboxPresent && (!usePortal || menuStyle !== null);
  useAnchorUpdates(shouldShowMenu && !exiting, buttonRef, measure);

  return (
    <div ref={containerRef} className={className}>
      {label !== '' && (
        <div className="tw-flex tw-items-center tw-gap-2">
          <FieldTitle
            label={label}
            required={required}
            /* § 355 — hidden, not removed: the trigger is still labelled by it. */
            className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
            size={labelSize}
            disabled={disabled}
            labelId={labelId}
          />
          {infoMessage && (
            <div className="tw-mb-2">
              <InfoTooltip message={infoMessage} placement="top" />
            </div>
          )}
        </div>
      )}

      <div className="tw-relative">
        <button
          /* rest first — see the rule in Button.tsx. This trigger owns `onClick`,
             `onKeyDown` and `onBlur`; spread last, a caller's stray handler would
             leave a dropdown that opens on neither click nor arrow key. */
          {...rest}
          type="button"
          ref={setButtonRef}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-labelledby={triggerLabelledBy}
          /*
            § 337 — `required` is a DESCRIPTION here, not `aria-required`. The trigger is a
            `<button>`, and `aria-required` is not allowed on the button role (axe
            `aria-allowed-attr`; assistive tech may ignore it). `role="combobox"`, where it
            IS allowed, was the other way out, but a combobox cannot take its name from its
            own text — every label-less Dropdown would have gone nameless — and every
            `getByRole('button')` selector would have broken. So the trigger stays a button
            and says "Required" through the same sr-only word FileUpload uses (§ 302).
          */
          aria-describedby={
            [
              /* § 374 — the value, for a trigger whose `aria-label` is its name. */
              namedByAriaLabel && selectedOption ? valueId : undefined,
              describedByProp,
              ...messageIds({ helperId, errorId }, { helperText, errorMessage }),
              additionalInfo ? infoId : undefined,
              required ? requiredId : undefined,
            ].filter(Boolean).join(' ') || undefined
          }
          aria-invalid={invalid || undefined}
          data-testid={dataTestId}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (disabled) return;
            /* § 406 — type-to-select on the CLOSED trigger, as a native select and the APG
               select-only combobox do: a printable key opens the list on the first option
               that starts with it, and the next keys continue that search inside the list.
               Space stays the button's (it opens the list); a modifier makes it a shortcut;
               a searchable list has its own filter box for typing into. */
            if (
              !isOpen && !searchable && event.key.length === 1 && event.key !== ' '
              && !event.ctrlKey && !event.metaKey && !event.altKey
            ) {
              event.preventDefault();
              measure();
              setIsOpen(true);
              focusOption('selected', event.key);
              return;
            }
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            // Standard combobox behaviour: an arrow opens the list and moves into
            // it, entering at the selected option so the current value is the
            // starting point rather than always the top of the list.
            event.preventDefault();
            if (!isOpen) {
              measure();
              setIsOpen(true);
              focusOption('selected');
            } else {
              focusOption(event.key === 'ArrowDown' ? 'first' : 'last');
            }
          }}
          onFocus={(e) => {
            triggerFocus.onFocus();
            onFocusProp?.(e);
          }}
          onBlur={(e) => {
            triggerFocus.onBlur();
            const next = e.relatedTarget as Element | null;
            /* § 304 — focus moving INTO this control's own portalled listbox is not a
               blur: the list is part of the control wherever the DOM puts it. Before,
               arrowing into a portalled list fired `onBlur` — and a form marking the
               field touched on blur showed its error while the user was still choosing. */
            const intoOwnList = !!next?.closest?.(`[data-dropdown-portal="${uid}"]`);
            if (!containerRef.current?.contains(next) && !intoOwnList) {
              onBlur?.();
            }
          }}
          {...retainingDisabled(disabled, holder === 'trigger')}
          /* Field chrome from the shared seam (`fieldChrome.ts`) — § 31 reversal. The
             extra `isOpen` arm keeps the trigger visibly ACTIVE while the menu is up:
             `FOCUS_FIELD` is a `:focus` treatment and the options take focus once the
             list opens, so without it the open menu floated over a field that had
             visibly gone back to sleep. `fieldBorder` owns the border colour so the
             open/error/rest borders never stack — two plain border classes on one
             element leave the winner to stylesheet order. */
          /* Height from the shared control grid, not from `py-2` + a line box + a border —
             that arithmetic is what made this 38px while `Button` declared 32. `py` is
             gone: a fixed height centres an input's text on its own, and on the flex
             trigger `items-center` does it. See `controlSize.ts`. */
          className={`tw-w-full ${CONTROL_HEIGHT[size]} ${FIELD_INSET[size]} ${FIELD_BOX} ${fieldBorder({ error: invalid, disabled, open: isOpen })} tw-text-body-primary tw-text-left tw-flex tw-items-center tw-justify-between
            ${disabled ? `${FIELD_DISABLED} ${FOCUS_FIELD} forced-colors:tw-text-[color:GrayText]` : FIELD_REST}
            ${isOpen && !disabled
              ? (invalid ? 'tw-ring-1 tw-ring-tone-danger-glyph' : 'tw-ring-1 tw-ring-accent-hover')
              : ''
            }
            ${invalid ? 'focus:tw-ring-tone-danger-glyph' : ''}
          `}
        >
          {/* Placeholder ink was `grey-500` — 2.64:1, an AA failure nobody had
              measured (SC 1.4.3 has no placeholder exemption). `ink-secondary`
              clears 4.5:1 on white AND on the hover tint. Disabled stays grey-500:
              inactive controls are exempt. */}
          {/*
            TRUNCATE, don't wrap (§ 124). The trigger is a fixed-height flex row
            (`CONTROL_HEIGHT`), so a label longer than the control wrapped to two or
            three lines and spilled ABOVE and BELOW the border — measured on the ISR
            filter row, where a category named "Enclosure Accessories Updated" struck
            through the label above it and the row beneath.

            `min-w-0` is the load-bearing half: a flex item's default `min-width:auto`
            refuses to shrink below its content, so `truncate` alone does nothing here.
            `title` keeps the full text reachable on hover, since ellipsis hides it.
          */}
          {/*
            The selected option's glyph rides the trigger too. Without it, choosing
            "Substitution Model B" from a list where the icon is the only thing
            distinguishing it from a Co-seller of the same name collapses back to
            plain text the moment the list closes — the distinction would exist only
            while you were choosing, not afterwards.

            OUTSIDE the truncating span, not inside it: `truncate` is
            `overflow:hidden` + `text-overflow:ellipsis`, which would clip the glyph
            first on a narrow trigger. `flex-shrink-0` keeps it whole and lets the
            label absorb the squeeze, which is the half that can degrade gracefully.
          */}
          {selectedOption?.icon && (
            <span aria-hidden="true" className="tw-flex tw-items-center tw-flex-shrink-0 tw-mr-2">
              {selectedOption.icon}
            </span>
          )}
          <span
            id={valueId}
            className={`tw-truncate tw-min-w-0 tw-flex-1 ${
              disabled ? 'tw-text-ink-disabled' : selectedOption ? 'tw-text-ink' : 'tw-text-ink-secondary'
            }`}
            title={selectedOption?.label || placeholder}
          >
            {selectedOption?.label || placeholder}
          </span>
          {/* § 303 — room for the clear ×, which is a SIBLING of this button (a button
              inside a button is invalid HTML) positioned over this gap. */}
          {showClear && <span aria-hidden="true" className="tw-w-6 tw-shrink-0" />}
          {/*
            Same glyph as `DropdownButton`, which drew a down-ARROW until 2026-08-24 while
            this drew a chevron — the two marked one affordance two ways, side by side.

            Colour is NAMED here rather than inherited. `DropdownButton` uses
            `currentColor` because its label colour is the variant's; this trigger's label
            is `grey-900` when a value is selected and `grey-500` when it is a placeholder,
            and a chevron that lightened whenever the field was empty would read as
            disabled. `ink-tertiary` (grey-600) is the design system's icon ink — 4.52:1 on white, the same
            token and the same reasoning as `Search`'s magnifier (SC 1.4.11).

            What this replaced, `MenuDownIcon`, hardcoded `stroke="#8E8E93"` — the *legacy*
            `gray-200`, 3.26:1, off-palette, and invisible to `tokenDiscipline` because the
            hex sat inside an imported SVG instead of in a class.
          */}
          <ChevronDown
            aria-hidden="true"
            className={`tw-flex-shrink-0 motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift ${
              disabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'
            } ${isOpen ? 'tw-rotate-180' : ''}`}
          />
        </button>
        {/* § 337 — the trigger's "Required" description. `sr-only` is absolutely
            positioned, so it takes no space in this row. */}
        {required && (
          <span id={requiredId} className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-required` : undefined}>
            Required
          </span>
        )}

        {/* § 303 — the clear ×: a real button (a keyboard user has no other way back to
            the placeholder), beside the chevron, centred with inset-y + auto margins
            rather than a transform (the TimePicker lesson: a transform here would make
            this a stacking context beside the listbox). `stopPropagation` so the click
            clears without also opening the list. */}
        {showClear && (
          <button
            type="button"
            aria-label="Clear selection"
            data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
            onClick={(event) => {
              event.stopPropagation();
              setValue(undefined);
              onClear?.();
            }}
            className={`tw-absolute tw-inset-y-0 tw-my-auto tw-end-8 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-ctl tw-text-ink-tertiary hover:tw-text-ink hover:tw-bg-surface-hover-strong motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING}`}
          >
            <Cross size={16} aria-hidden="true" />
          </button>
        )}

        {shouldShowMenu && (
          usePortal ? createPortal(menuContent, document.body) : menuContent
        )}
      </div>

      {/* § 302 — through the one helper seam: an errored Dropdown now ANNOUNCES like an
          errored TextField (role="alert", remounted), where it used to rely on
          `aria-describedby` alone and stay silent until the trigger took focus. */}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-1"
        data-testid={dataTestId}
      />

      {additionalInfo && (
        <Typography
          id={infoId}
          variant="caption-metadata"
          className="tw-mt-2 tw-text-ink-tertiary"
        >
          {additionalInfo}
        </Typography>
      )}

      {/* § 406 — the filter's result count (see `resultsSpoken`). */}
      {searchable && isOpen && (
        <span role="status" className="tw-sr-only" data-testid={dataTestId ? `${dataTestId}-status` : undefined}>
          {resultsSpoken}
        </span>
      )}

      {/* § 406 — `name` carries the VALUE into a form, as Combobox's does. It used to land on
          the trigger `<button>`, which submits nothing. */}
      {name !== undefined && (
        <input type="hidden" name={name} value={value === undefined || value === null ? '' : String(value)} />
      )}
    </div>
  );
});

/* § 389 — `forwardRef` erases a type parameter; the cast restores `V`, so `<Dropdown<Unit> … />`
   types its value. Without the argument `V` is `string | number`, exactly as before. */
export default Dropdown as <V extends string | number = string | number>(
  props: DropdownProps<V> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactElement | null;
